import type { PackageFileContent, PackageFileInfo } from './packageTypes';

import { apiRequest } from './base';

export type Skillset = {
  name: string;
  version?: string | null;
  description?: string;
  vouchStatus?: string | null;
  [key: string]: any;
};

/**
 * Lists all available skillsets from the registry
 *
 * @returns Promise resolving to array of Skillset objects
 */
export const listSkillsets = async (): Promise<Array<Skillset>> => {
  return apiRequest<Array<Skillset>>('/skillsets/all', {
    method: 'GET',
  });
};

/**
 * Gets details for a specific skillset by name
 *
 * @param name - The skillset name to fetch
 *
 * @returns Promise resolving to Skillset object
 */
export const getSkillset = async (name: string): Promise<Skillset> => {
  return apiRequest<Skillset>(`/skillsets/${name}`, {
    method: 'GET',
  });
};

/**
 * Searches for skillsets matching the query
 *
 * @param query - Search query string
 *
 * @returns Promise resolving to array of matching skillsets
 */
export const searchSkillsets = async (
  query: string,
): Promise<Array<Skillset>> => {
  return apiRequest<Array<Skillset>>(
    `/search?q=${encodeURIComponent(query)}`,
    {
      method: 'GET',
    },
  );
};

/**
 * Resolution choice for a single skill conflict
 */
export type SkillResolution = {
  action: 'namespace' | 'updateVersion' | 'link';
  version?: string;
};

/**
 * Resolution choice for a single subagent conflict
 */
export type SubagentResolution = {
  action: 'namespace' | 'updateVersion' | 'link';
  version?: string;
};

/**
 * Arguments for uploadSkillset
 */
export type UploadSkillsetArgs = {
  skillsetName: string;
  file: File;
  version: string;
  description?: string;
  releaseNotes?: string;
  keywords?: Array<string>;
  license?: string;
  repository?: string;
  resolutionStrategy?: Record<string, SkillResolution>;
  inlineSkills?: Array<string> | null;
  subagentResolutionStrategy?: Record<string, SubagentResolution>;
  inlineSubagents?: Array<string> | null;
  vouchStatus?: string | null;
};

/**
 * Uploads a skillset (folder/zip/tarball)
 * Server will generate nori.json manifest from skillset structure
 *
 * @param args - Upload arguments
 * @param args.skillsetName - The skillset name
 * @param args.file - The archive file (tarball, zip, or folder)
 * @param args.version - The version string (semver)
 * @param args.description - Optional skillset description
 * @param args.releaseNotes - Optional release notes for this version
 *
 * @returns Promise resolving to the upload response
 */
export const uploadSkillset = async (
  args: UploadSkillsetArgs,
): Promise<any> => {
  const {
    skillsetName,
    file,
    version,
    description,
    releaseNotes,
    keywords,
    license,
    repository,
    resolutionStrategy,
    inlineSkills,
    subagentResolutionStrategy,
    inlineSubagents,
    vouchStatus,
  } = args;
  const formData = new FormData();
  formData.append('archive', file);
  formData.append('version', version);
  if (description) {
    formData.append('description', description);
  }
  if (releaseNotes) {
    formData.append('releaseNotes', releaseNotes);
  }
  if (keywords && keywords.length > 0) {
    formData.append('keywords', JSON.stringify(keywords));
  }
  if (license) {
    formData.append('license', license);
  }
  if (repository) {
    formData.append('repository', repository);
  }
  if (resolutionStrategy && Object.keys(resolutionStrategy).length > 0) {
    formData.append('resolutionStrategy', JSON.stringify(resolutionStrategy));
  }
  if (inlineSkills != null) {
    formData.append('inlineSkills', JSON.stringify(inlineSkills));
  }
  if (
    subagentResolutionStrategy &&
    Object.keys(subagentResolutionStrategy).length > 0
  ) {
    formData.append(
      'subagentResolutionStrategy',
      JSON.stringify(subagentResolutionStrategy),
    );
  }
  if (inlineSubagents != null) {
    formData.append('inlineSubagents', JSON.stringify(inlineSubagents));
  }
  if (vouchStatus != null) {
    formData.append('vouchStatus', vouchStatus);
  }

  return apiRequest(`/skillsets/${skillsetName}/profile`, {
    method: 'PUT',
    headers: {
      // Don't set Content-Type - browser will set it with boundary for multipart
    },
    body: formData,
  });
};

export type SkillsetMaintainer = {
  email: string;
  displayName: string;
  role: 'owner' | 'maintainer';
  addedAt: string;
};

/**
 * Fetch maintainers for a skillset
 *
 * @param skillsetName - The skillset name
 *
 * @returns Promise resolving to array of skillset maintainers
 */
export const fetchSkillsetMaintainers = async (
  skillsetName: string,
): Promise<{ maintainers: Array<SkillsetMaintainer> }> => {
  return apiRequest<{ maintainers: Array<SkillsetMaintainer> }>(
    `/skillsets/${skillsetName}/maintainers`,
    {
      method: 'GET',
    },
  );
};

/**
 * Adds a maintainer to a skillset (owner only)
 *
 * @param args - Arguments
 * @param args.skillsetName - The skillset name
 * @param args.email - Email of the user to add
 * @param args.role - Role to assign ('owner' or 'maintainer')
 *
 * @returns Promise resolving to success response
 */
export const addSkillsetMaintainer = async (args: {
  skillsetName: string;
  email: string;
  role: 'owner' | 'maintainer';
}): Promise<{ success: boolean }> => {
  const { skillsetName, email, role } = args;
  return apiRequest<{ success: boolean }>(
    `/skillsets/${skillsetName}/maintainers/${email}`,
    {
      method: 'PUT',
      body: JSON.stringify({ role }),
    },
  );
};

/**
 * Removes a maintainer from a skillset (owner only)
 *
 * @param args - Arguments
 * @param args.skillsetName - The skillset name
 * @param args.email - Email of the user to remove
 *
 * @returns Promise resolving to success response
 */
export const removeSkillsetMaintainer = async (args: {
  skillsetName: string;
  email: string;
}): Promise<{ success: boolean }> => {
  const { skillsetName, email } = args;
  return apiRequest<{ success: boolean }>(
    `/skillsets/${skillsetName}/maintainers/${email}`,
    {
      method: 'DELETE',
    },
  );
};

/**
 * Arguments for updateSkillsetMetadata
 */
export type UpdateSkillsetMetadataArgs = {
  skillsetName: string;
  version: string;
  description?: string;
  releaseNotes?: string;
  keywords?: Array<string>;
};

/**
 * Response from updateSkillsetMetadata
 */
export type UpdateSkillsetMetadataResponse = {
  version: string;
  metadata: Record<string, unknown>;
  releaseNotes: string | null;
};

/**
 * Update metadata for a skillset version and create a new patch version
 *
 * @param args - Update arguments
 * @param args.skillsetName - The skillset name
 * @param args.version - The source version to update
 * @param args.description - Optional new description
 * @param args.releaseNotes - Optional new release notes
 * @param args.keywords - Optional new keywords array
 *
 * @returns Promise resolving to the new version info
 */
export const updateSkillsetMetadata = async (
  args: UpdateSkillsetMetadataArgs,
): Promise<UpdateSkillsetMetadataResponse> => {
  const { skillsetName, version, description, releaseNotes, keywords } = args;

  const body: Record<string, unknown> = {};
  if (description !== undefined) {
    body.description = description;
  }
  if (releaseNotes !== undefined) {
    body.releaseNotes = releaseNotes;
  }
  if (keywords !== undefined) {
    body.keywords = keywords;
  }

  return apiRequest<UpdateSkillsetMetadataResponse>(
    `/skillsets/${skillsetName}/versions/${version}/metadata`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
};

/**
 * Sets the vouch status for a skillset (admin only)
 *
 * @param args - Arguments
 * @param args.skillsetName - The skillset name
 * @param args.vouchStatus - The vouch status to set
 * @param args.reason - The denial reason; required by the server when denying
 *
 * @returns Promise resolving to the updated vouch status
 */
export const setSkillsetVouchStatus = async (args: {
  skillsetName: string;
  vouchStatus: string;
  reason?: string | null;
}): Promise<{ name: string; vouchStatus: string }> => {
  const { skillsetName, vouchStatus, reason } = args;
  return apiRequest<{ name: string; vouchStatus: string }>(
    `/skillsets/${skillsetName}/vouch-status`,
    {
      method: 'PUT',
      body: JSON.stringify({ vouchStatus, reason }),
    },
  );
};

/**
 * The per-constituent outcome of a whole-skillset review cascade. `skipped_shared`
 * means the constituent was left untouched because another approved skillset still
 * depends on it.
 */
export type SkillsetReviewItem = {
  type: 'skillset' | 'skill' | 'subagent';
  name: string;
  result: 'approved' | 'denied' | 'skipped_shared';
  vouchStatus: string;
  sharedWith?: Array<string>;
};

export type SkillsetReviewResult = {
  skillset: SkillsetReviewItem;
  constituents: Array<SkillsetReviewItem>;
  summary: { changed: number; skipped: number };
};

/**
 * Approve or deny a skillset and all of its bundled skills/subagents in a single
 * action (admin only). A deny skips any constituent still used by another
 * approved skillset.
 *
 * @param args - Arguments
 * @param args.skillsetName - The skillset name
 * @param args.decision - 'approve' or 'deny'
 * @param args.reason - The denial reason; required by the server when denying
 *
 * @returns Promise resolving to the per-constituent review result
 */
export const reviewSkillset = async (args: {
  skillsetName: string;
  decision: 'approve' | 'deny';
  reason?: string | null;
}): Promise<SkillsetReviewResult> => {
  const { skillsetName, decision, reason } = args;
  return apiRequest<SkillsetReviewResult>(
    `/skillsets/${skillsetName}/review`,
    {
      method: 'POST',
      body: JSON.stringify({ decision, reason }),
    },
  );
};

/**
 * Sets the featured status for a skillset (admin only)
 *
 * @param args - Arguments
 * @param args.skillsetName - The skillset name
 * @param args.featured - Whether the skillset should be featured
 *
 * @returns Promise resolving to the updated featured status
 */
export const setSkillsetFeatured = async (args: {
  skillsetName: string;
  featured: boolean;
}): Promise<{ name: string; featured: boolean }> => {
  const { skillsetName, featured } = args;
  return apiRequest<{ name: string; featured: boolean }>(
    `/skillsets/${skillsetName}/featured`,
    {
      method: 'PUT',
      body: JSON.stringify({ featured }),
    },
  );
};

/**
 * Response from deleteSkillset
 */
export type DeleteSkillsetResponse = {
  message: string;
};

/**
 * Deletes a skillset and all its versions (admin only)
 *
 * @param args - Delete arguments
 * @param args.skillsetName - The skillset name to delete
 *
 * @returns Promise resolving to the deletion response
 */
export const deleteSkillset = async (args: {
  skillsetName: string;
}): Promise<DeleteSkillsetResponse> => {
  const { skillsetName } = args;
  return apiRequest<DeleteSkillsetResponse>(`/skillsets/${skillsetName}`, {
    method: 'DELETE',
  });
};

/**
 * Renames a skillset
 *
 * @param args - Rename arguments
 * @param args.skillsetName - The current skillset name
 * @param args.newName - The new skillset name
 *
 * @returns Promise resolving to the old and new names
 */
export const renameSkillset = async (args: {
  skillsetName: string;
  newName: string;
}): Promise<{ oldName: string; newName: string }> => {
  const { skillsetName, newName } = args;
  return apiRequest<{ oldName: string; newName: string }>(
    `/skillsets/${skillsetName}/rename`,
    {
      method: 'PATCH',
      body: JSON.stringify({ newName }),
    },
  );
};

/**
 * Response from reingestSkillsetMetadata
 */
export type ReingestSkillsetMetadataResponse = {
  version: string;
  metadata: Record<string, unknown>;
  message: string;
};

/**
 * Re-extract metadata from the stored tarball and update the version (admin only)
 *
 * @param args - Reingest arguments
 * @param args.skillsetName - The skillset name
 * @param args.version - The version to reingest metadata for
 *
 * @returns Promise resolving to the reingest response
 */
export const reingestSkillsetMetadata = async (args: {
  skillsetName: string;
  version: string;
}): Promise<ReingestSkillsetMetadataResponse> => {
  const { skillsetName, version } = args;
  return apiRequest<ReingestSkillsetMetadataResponse>(
    `/skillsets/${skillsetName}/versions/${version}/reingest`,
    {
      method: 'POST',
    },
  );
};

export type AnalysisResult = {
  status: 'pending' | 'completed' | 'failed';
  suggestions: {
    lint: Array<{
      rule: string;
      file: string;
      line: number | null;
      message: string;
      snippet: string | null;
    }>;
    dependencies: Array<{
      type: string;
      skillName: string;
      file: string;
      message: string;
      existsInRegistry: boolean;
    }>;
  } | null;
};

export const getSkillsetAnalysis = async (args: {
  name: string;
  version: string;
}): Promise<AnalysisResult | null> => {
  const { name, version } = args;
  try {
    return await apiRequest<AnalysisResult>(
      `/skillsets/${name}/${version}/analysis`,
      { method: 'GET' },
    );
  } catch {
    return null;
  }
};

export type { PackageFileInfo, PackageFileContent } from './packageTypes';

/**
 * Lists all non-binary files in a skillset version's package
 *
 * @param args - Arguments
 * @param args.name - The skillset name
 * @param args.version - The version string
 *
 * @returns Promise resolving to array of file info objects
 */
export const getSkillsetFiles = async (args: {
  name: string;
  version: string;
}): Promise<{ files: Array<PackageFileInfo> }> => {
  const { name, version } = args;
  return apiRequest<{ files: Array<PackageFileInfo> }>(
    `/skillsets/${name}/${version}/files`,
    {
      method: 'GET',
    },
  );
};

/**
 * Gets content of a specific file from a skillset version's package
 *
 * @param args - Arguments
 * @param args.name - The skillset name
 * @param args.version - The version string
 * @param args.filePath - The file path within the package
 *
 * @returns Promise resolving to file content object
 */
export const getSkillsetFileContent = async (args: {
  name: string;
  version: string;
  filePath: string;
}): Promise<PackageFileContent> => {
  const { name, version, filePath } = args;
  return apiRequest<PackageFileContent>(
    `/skillsets/${name}/${version}/files/${filePath}`,
    {
      method: 'GET',
    },
  );
};
